import classnames from 'classnames/bind'
import { useMemo, useRef } from 'react'
import { GlobalGridPreset } from '~/@types/grid-preset'
import { OriginType, TRACKING_EVENTS } from '~/lib/constants'

import { useIsomorphicLayoutEffect } from '@unlikelystudio/react-hooks'

import { ProductStatusTags } from '~/components/UI/ProductCard/types'
import ProductDetails, {
  ProductDetailsProps,
} from '~/components/UI/ProductHeader/ProductDetails'
import ProductHeaderImages from '~/components/UI/ProductHeader/ProductHeaderImages'
import ProductHeaderTags from '~/components/UI/ProductHeader/ProductHeaderTags'

import { useShopifyProductContext } from '~/providers/ShopifyProductProvider'
import { useStyle } from '~/providers/StyleProvider'
import { useTracker } from '~/providers/TrackerProvider'

import { fromProductCardPropsToProductTrackingData } from '~/hooks/tracking/useTrackProductCardsList'
import useFallbackVariants from '~/hooks/useFallbackVariants'
import useVariant from '~/hooks/useVariant'

import deepEqualCompareVariants from '~/utils/deep-equal-compare-variants'

import css from './styles.module.scss'

const cx = classnames.bind(css)

export type ProductHeaderProps = ProductDetailsProps & {
  className?: string
  productStatusTags?: ProductStatusTags
  sustainabilityTags?: string[]
  customTags?: string[]
}

function ProductHeader({
  className,
  items,
  mediaItems,
  variantsItems,
  productVariations,
  productStatusTags,
  sustainabilityTags,
  trackingData,
  customTags,
  ...rest
}: ProductHeaderProps) {
  const gridStyle = useStyle({ grid: GlobalGridPreset.BASE_GRID })

  const firstLoad = useRef(true)
  const tracker = useTracker()
  const {
    variants: updatedVariants,
    defaultVariant: updatedDefaultVariant,
    isLoading,
  } = useShopifyProductContext()

  const isVariantsEqual = deepEqualCompareVariants(
    updatedVariants,
    rest?.variants,
  )

  const { fallbackVariantValues, compiledVariantProps } = useFallbackVariants({
    baseVariants: rest?.variants,
    baseDefaultVariant: rest?.defaultVariant,
    updatedVariants,
    updatedDefaultVariant,
    isUpdatedVariantsLoaded: !isLoading,
    isVariantsEqual,
  })

  // Processed variant from routing param
  const { selectedVariant, variantIndex, setSelectedVariant } = useVariant(
    fallbackVariantValues?.variants,
    fallbackVariantValues?.defaultVariant,
  )

  const variantTrackingData = useMemo(() => {
    const price = +selectedVariant?.unformattedPrice?.amount

    return {
      ...(fromProductCardPropsToProductTrackingData(
        {
          variantId: selectedVariant?.id ?? null,
          sku: selectedVariant?.sku ?? '',
          size: selectedVariant?.title ?? null,
          priceAmount: !isNaN(price) ? price : null,
        },
        {},
      ) ?? {}),
      ...trackingData,
    }
  }, [rest, selectedVariant])

  const triggerEvent = (event: string) => {
    tracker.emit<typeof event>(event, {
      origin: OriginType.DETAIL_VIEW,
      item: variantTrackingData,
    })
  }

  useIsomorphicLayoutEffect(() => {
    if (selectedVariant && firstLoad.current) {
      firstLoad.current = false
      setTimeout(() => triggerEvent(TRACKING_EVENTS.PRODUCT_PAGE_VIEW), 0)
    }
  }, [selectedVariant])

  const mediaGallery = items && items?.length > 0 ? [...items] : []

  return (
    <div className={cx(css.ProductHeader, className, gridStyle)}>
      <ProductDetails
        className={cx(css.details)}
        setSelectedVariant={setSelectedVariant}
        selectedVariant={selectedVariant ?? rest?.defaultVariant}
        productVariations={productVariations}
        onAddToCart={() => triggerEvent(TRACKING_EVENTS.PRODUCT_ADD_TO_CART)}
        trackingData={variantTrackingData}
        firstImage={mediaGallery?.[0]?.image ?? null}
        {...rest}
        {...compiledVariantProps}
      />

      {mediaGallery?.length > 0 && (
        <>
          <ProductHeaderImages
            className={css.images}
            items={mediaGallery}
            mediaItems={mediaItems}
            variantsItems={variantsItems}
            variantIndex={variantIndex}
            productName={rest?.productName}
          />
          {(productStatusTags || sustainabilityTags || customTags) && (
            <ProductHeaderTags
              className={cx(css.tags)}
              {...{
                productStatusTags: productStatusTags ?? null,
                sustainabilityTags: sustainabilityTags ?? null,
                customTags: customTags ?? [],
              }}
            />
          )}
        </>
      )}
    </div>
  )
}

ProductHeader.defaultProps = {}

export default ProductHeader
